import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { ApiResponse } from '@/types/api'
import { API_BASE } from '@/config/deploy'

const baseURL = API_BASE

let csrfToken = ''

export function setCsrfToken(token: string) {
  if (token) csrfToken = token
}

export function getCsrfToken() {
  return csrfToken
}

export const api = axios.create({
  baseURL,
  timeout: 60000,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Join API_BASE + path even when path starts with "/".
  // Needed for PATH_INFO deploys: base=/api/index.php + /auth/providers
  // → /api/index.php/auth/providers (axios alone would drop the base).
  const base = (baseURL || '').replace(/\/$/, '')
  const url = config.url || ''
  if (base && url.startsWith('/')) {
    config.url = base + url
    config.baseURL = ''
  }

  const method = (config.method || 'get').toUpperCase()
  if (csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    config.headers.set('X-CSRF-Token', csrfToken)
  }
  // Let the browser set multipart boundary for FormData
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type')
  }
  return config
})

api.interceptors.response.use(
  (response) => {
    const body = response.data as ApiResponse & { csrf_token?: string }
    if (body && typeof body.csrf_token === 'string') {
      setCsrfToken(body.csrf_token)
    }
    const headerToken = response.headers['x-csrf-token']
    if (typeof headerToken === 'string' && headerToken) {
      setCsrfToken(headerToken)
    }
    return response
  },
  (error: AxiosError<ApiResponse & { csrf_token?: string }>) => {
    if (error.response?.data?.csrf_token) {
      setCsrfToken(error.response.data.csrf_token)
    }
    if (error.response?.status === 401) {
      const reqUrl = String(error.config?.url || '')
      // Auth endpoints handle their own errors (bad password, etc.)
      const isAuthCall =
        /\/auth\/(login|mfa|providers|logout)/i.test(reqUrl) ||
        reqUrl.endsWith('/auth/login') ||
        reqUrl.includes('/auth/login')
      const hash = window.location.hash || ''
      const onLoginPage = hash.includes('/login') || hash === '#/login'
      if (!isAuthCall && !onLoginPage) {
        // Modal re-auth with blurred app (see SessionExpiredModal)
        window.dispatchEvent(
          new CustomEvent('appcenter:session-expired', {
            detail: { message: error.response.data?.message || 'Session expired' },
          }),
        )
      }
    }
    // Suspended org: let the app show a clear message (data.code === tenant_suspended)
    if (error.response?.status === 403) {
      const payload = error.response.data?.data as { code?: string } | undefined
      if (payload && payload.code === 'tenant_suspended') {
        window.dispatchEvent(
          new CustomEvent('appcenter:tenant-suspended', {
            detail: {
              message: error.response.data?.message,
              ...(typeof payload === 'object' ? payload : {}),
            },
          }),
        )
      }
    }
    return Promise.reject(error)
  },
)

/**
 * Rewrite absolute monorepo API links (/na/api/v1/…) to the current deploy base.
 * PHP now emits correct bases; this covers cached responses and older data.
 */
export function resolveApiAssetUrl(url: string | null | undefined): string {
  if (!url) return ''
  const u = String(url)
  if (u.startsWith('/na/api/v1')) {
    return u.replace(/^\/na\/api\/v1/, baseURL.replace(/\/$/, '') || '/api')
  }
  if (u.startsWith('/api/') && baseURL.includes('/api/index.php') && !u.startsWith('/api/index.php')) {
    return u.replace(/^\/api/, baseURL.replace(/\/$/, ''))
  }
  return u
}

export async function getJson<T>(url: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
  const { data } = await api.get<ApiResponse<T>>(url, { params })
  return data
}

export async function postJson<T>(url: string, body?: Record<string, unknown>): Promise<ApiResponse<T>> {
  const { data } = await api.post<ApiResponse<T>>(url, body ?? {})
  return data
}

export async function patchJson<T>(url: string, body?: Record<string, unknown>): Promise<ApiResponse<T>> {
  const { data } = await api.patch<ApiResponse<T>>(url, body ?? {})
  return data
}

export async function deleteJson<T>(url: string): Promise<ApiResponse<T>> {
  const { data } = await api.delete<ApiResponse<T>>(url)
  return data
}

export async function postForm<T>(url: string, form: FormData): Promise<ApiResponse<T>> {
  const { data } = await api.post<ApiResponse<T>>(url, form, {
    timeout: 120000,
  })
  return data
}
